// ════════════════════════════════════════════════════════════════════════════
//  ManDana — a dropped document, and where its bytes are
//  ---------------------------------------------------------------------------
//  A PDF, or a Craft, somebody dropped on a desk: its bytes, where they live,
//  and what the thing that names it carries. md-board.jsx draws one and
//  md-app.jsx writes the record; both ask this.
//
//  ── THE ONE PLACE "STORAGE UNIT = SYNC UNIT" WAS SET ASIDE, AND IS NOT NOW ─
//  Every other document in this app is JSON in localStorage and travels through
//  md-sync.js. A PDF cannot: localStorage gives the whole ORIGIN about 5 MB for
//  everything ever written, server/sync.js caps one document at 1 MB, and a
//  modest PDF is larger than both put together. There is no arrangement of the
//  existing storage that holds one.
//
//  So the bytes went to IndexedDB — measured in hundreds of megabytes — and
//  they STAYED THERE. That was Marwan's call, made explicitly, and it was made
//  conditional: no sync for unsyncable files until there is a service that can
//  carry them. What it cost was stated rather than hidden, because the RECORD
//  synced in full, carrying the file's name, type and size. Every other device
//  knew the file existed, knew what it was called, and could not open it.
//
//  md-blobs.ts is that service, and story 1.9 spends it. A document's bytes now
//  go through the one door: kept on this device, named by their SHA-256, and
//  offered to whatever store mdTransportName() answers. The reference a record
//  carries gains that name, so a device that has never seen the bytes can ask
//  for them by it. What is left of the old exception is the honest half — a
//  device that does not have the bytes yet says so rather than drawing a broken
//  frame, which is the same posture md-board.ts takes with an oversized board:
//  keep it, name it, never pretend.
//
//  ── THE OFFER IS NOT AWAITED, AND THAT IS THE DESIGN ──────────────────────
//  Dropping a file is instant today and has to stay instant. The write below is
//  what makes the file safe; the upload is the door's business afterwards — it
//  caches before it reaches for a network, keeps what the store has not taken
//  on a pending list, and never lets a trim touch that. Awaiting it here would
//  hang a drop on an upload with no timeout under it, which is the one way to
//  turn a working feature into a spinner. mdFilePut hands the promise back as
//  `upload` for a caller that wants the store's answer; nobody has to take it.
//
//  WHAT THAT COSTS, said plainly: the device that dropped the file holds the
//  bytes TWICE — once in the database below, once in the door's cache. This
//  store is not trimmable and the door's is, so keeping both is what makes "it
//  is on your device" still true for somebody who freed space and then went
//  offline. It ends when md-blobs.ts can report that its cache took the bytes
//  separately from what the store answered; at that point this module stops
//  keeping bytes at all and becomes only what its name says.
//
//  ── TWO NAMES, AND NEITHER DOES THE OTHER'S JOB ───────────────────────────
//  `id` is where the bytes sit in THIS device's database. `hash` is what a
//  store calls them. For anything dropped from today they are the same string,
//  because the `mdfile:` line in a record carries exactly one token and it has
//  to be the one every device can resolve.
//
//  Every reference written before today has an `id` from mdFileHash and no
//  `hash` at all, and those keep working FOR EVER — they are written into
//  records people already have, and rewriting somebody's prose to repair our
//  storage would be the wrong trade twice over. An id that is not 64 hex
//  characters is simply a name no store has heard of: mdFileTravels says so,
//  the device holding the bytes still opens them, and the others say where they
//  are. mdFileHash is not deprecated either — it names a Craft, and it names a
//  document on any build that cannot compute a SHA-256. md-transport.ts's
//  header argues why the two hashes are two jobs and not one.
//
//  ── HOW A RECORD NAMES ONE — AND THE ARGUMENT THIS FILE USED TO MAKE ──────
//  This header used to argue at length that the reference had to be a FIELD on
//  the record rather than part of its text, because `mdfile:9fx2k3j1` is a
//  content hash and putting a hash in somebody's writing is gibberish in their
//  own words. That was reversed: it is a line of the writing now,
//  `![Al-Ghazali.pdf](mdfile:9fx2k3j1)`, and md-embed.ts:47 carries the argument
//  for the reversal — the line is visible, movable and deletable like everything
//  else a person wrote, and deleting it is how a document is removed. Records
//  written before that fold in through mdEmbedLegacy and are never rewritten.
//
//  The token in that line got longer today — a store name is 64 characters
//  where the old local one was around a dozen — and nothing had to change to
//  allow it: md-embed.ts has always accepted 6 to 64, and the name is never
//  rendered as text, because the line renders as the document.
//
//  ── THE TWO KINDS OF FILE, AND WHY THEY MUST NOT MEET ─────────────────────
//  A PDF is drawn by md-embed.jsx's FileFrame at a blob: URL with no sandbox.
//  A blob: frame inherits the creating origin, so its contents are same-origin
//  with this app — fine for a document the browser renders and never executes.
//
//  HTML is a program. It is accepted here, but into a separate list, and it is
//  drawn only by md-manifest.jsx's CraftFrame: srcdoc, sandboxed, no
//  allow-same-origin, opaque origin. Nothing in the app ever writes an `mdfile:`
//  line pointing at one — a Craft is not an embed at all — so the two paths
//  cannot meet by accident. FileFrame refuses a non-PDF Blob anyway, because
//  "cannot meet by accident" is not the same as "cannot meet".
//
//  A STORE MAKES THAT SHARPER, so there is a third guard now. Only what a
//  record may name is ever offered to one — a Craft's bytes stay here, which is
//  story 1.10's to change — and bytes coming BACK are typed by their own first
//  five, never by what the store said they were. The one remaining way to aim
//  an unsandboxed frame at a program is somebody hand-writing an `mdfile:` line
//  at the hash of an HTML document, and mdFileSniff answers that with nothing.
// ════════════════════════════════════════════════════════════════════════════

/** What a record carries about the file it names. Small, and all of it is
    useful WITHOUT the bytes — which is the point, because a device that has
    just met the record has not fetched them yet, and may never. */
interface MdFileRef {
  id: string;
  name: string;
  type: string;
  size: number;
  /** What a store calls these bytes: SHA-256, lowercase hex. Absent on every
      reference written before 1.9, and on any build that could not compute
      one; both open their local copy exactly as they always did. */
  hash?: string;
  /** The marker md-barrier.ts walks for. A document that names bytes must SAY
      that it does, or the barrier is left guessing which of its hash-shaped
      fields are blobs — and a wrong guess holds a document back from syncing
      for ever, silently. Written only beside a real `hash`. */
  blob?: 1;
}

(function (g: any) {
  'use strict';

  const DB_NAME = 'mandana-files';
  const DB_VERSION = 1;
  const STORE = 'files';

  /* Resolved once and remembered for the rest of the page load, never
     recomputed. Adoption decides which side of the legacy/scoped line this
     account fell on at boot, before anything opens a database — a name that
     could change mid-session would be a device that loses track of files it
     already has partway through opening the store that holds them.

     Called defensively: md-adopt.ts loads before this module in the app, but
     not in every test harness, and the identity default — the bare name — is
     exactly right for a page that has not decided otherwise yet. */
  let dbName: string | null = null;
  function nameOf(): string {
    if (dbName === null) {
      const asked: unknown = (typeof g.mdCompartmentDbName === 'function')
        ? g.mdCompartmentDbName(DB_NAME) : DB_NAME;
      dbName = typeof asked === 'string' && asked ? asked : DB_NAME;
    }
    return dbName;
  }

  /* What may be taken in. A denylist is a promise to have thought of every
     dangerous type and we have not, so this is an allowlist of things we can
     actually DRAW — the only reason to accept a file at all.

     TWO LISTS, AND THEY MUST NEVER MERGE. The types below are drawn by
     md-embed.jsx's FileFrame, which frames them at a blob: URL with NO sandbox —
     a blob: frame inherits the creating origin, so whatever is in it is
     same-origin with this app, its passkey session and its storage. That is
     fine for a PDF, which the browser renders as a document and never as a
     program. It would be the maximum-severity version of this whole feature for
     anything that executes.

     So HTML is accepted, and it is accepted into a DIFFERENT list. The type
     alone decides which frame may draw it, and tests/file.test.js asserts that
     mdFileKind('text/html') can never come back 'pdf' — the two cannot be
     confused by a later edit that means well. */
  const MD_FILE_TYPES: Record<string, string> = {
    'application/pdf': 'pdf',
  };

  /* Drawn only by md-manifest.jsx's CraftFrame: srcdoc, sandboxed, no
     allow-same-origin, opaque origin. Never by FileFrame. */
  const MD_CRAFT_TYPES: Record<string, string> = {
    'text/html': 'html',
  };

  /** The type a manifestation is given when nothing hands one over — a paste,
      where there is text on the clipboard and no file to ask.

      It lives here rather than at the call site because a MIME string written
      inline next to `new File(…)` is a second allowlist, and the two would drift
      the first time this one gained an entry. The board asks this module what a
      manifestation is called, the same way it asks it what may be stored. */
  const MD_CRAFT_MIME = 'text/html';

  /* Bounded because IndexedDB is generous, not infinite, and because a file
     that cannot travel is one somebody may be surprised to lose with a browser
     profile. 40 MB is a large book and a small video; past that the honest
     answer is that this app is not where it belongs. */
  const MD_FILE_MAX_BYTES = 40 * 1024 * 1024;

  /* A name is a label in a list, the same rule md-record.ts applies to a title. */
  const NAME_MAX = 60;

  /* Lowercase hex, matching md-transport.ts, md-barrier.ts and server/blobs.js.
     One spelling, or the same bytes have two names and a store holds them
     twice. */
  const SHA256 = /^[0-9a-f]{64}$/;

  function mdFileKind(type: unknown): string {
    if (typeof type !== 'string') return '';
    return MD_FILE_TYPES[type] || MD_CRAFT_TYPES[type] || '';
  }

  /** Whether this type may ONLY be drawn in the sandboxed manifest frame. The
      question every drawing path has to ask before it frames anything, and the
      reason it is a function rather than a comparison is that a second manifest
      type should never mean hunting for `=== 'html'` across the app. */
  function mdFileIsCraft(type: unknown): boolean {
    return typeof type === 'string' && !!MD_CRAFT_TYPES[type];
  }

  /** Is this a complete, usable reference for a RECORD to carry? Asked before
      anything is drawn.

      It checks MD_FILE_TYPES rather than mdFileKind, and the difference is the
      whole point: mdFileKind says "the store will hold this", and this says "a
      record may name this". A manifestation is stored here and is never named by
      a record — it is its own thing in the library, reached by a ref — so a
      reference claiming to be one is malformed by construction, and the only way
      it could exist is somebody hand-writing an `mdfile:` line at HTML. That line
      would be drawn by an unsandboxed same-origin frame, so this is the earliest
      place to say no. */
  function mdFileRefOk(ref: unknown): boolean {
    const r = ref as MdFileRef | null;
    return !!r && typeof r === 'object'
      && typeof r.id === 'string' && /^[a-z0-9]{6,64}$/.test(r.id)
      && typeof r.name === 'string' && !!r.name
      && typeof r.type === 'string' && !!MD_FILE_TYPES[r.type]
      && typeof r.size === 'number' && r.size > 0
      /* Absent is the ordinary answer — every reference written before 1.9 has
         no store name and is perfectly usable. Present and malformed is not:
         a hash that is not a hash reaches a bucket key, and the earliest place
         to refuse one is before a record is allowed to carry it. */
      && (r.hash === undefined || (typeof r.hash === 'string' && SHA256.test(r.hash)));
  }

  /** Is this a name a store can be asked for, or only a name this device knows?

      The whole of the legacy story, in one predicate. Ids minted before 1.9 are
      mdFileHash's — two 32-bit hashes and a length in base36, around a dozen
      characters — and no store has ever heard of them. Asked before a fetch so
      an old reference costs no round trip, and asked by md-embed.jsx so it can
      say which of the two true sentences applies. */
  function mdFileTravels(id: unknown): boolean {
    return typeof id === 'string' && SHA256.test(id);
  }

  /* The reference a hold is recorded against, checked against the grammar
     server/blobs.js checks: `board:<id>` for a desk's own drawing, `record:<id>`
     for a file somebody dropped. Naming none is a hold for the ACCOUNT, which
     is what every row written before migration 0011 means, and which is the
     conservative rule Phase 1 ships — only account deletion reclaims bytes. */
  const OWNER = /^[a-z][a-z0-9]{0,15}:[a-z0-9]{1,64}$/;

  function ownerOf(opts: unknown): string {
    const named = (opts && typeof opts === 'object') ? (opts as any).ref : null;
    return (typeof named === 'string' && OWNER.test(named)) ? named : '';
  }

  /** What these bytes actually are, read from the bytes.

      NOT from the store's word for it, and this is the reason the whole
      allowlist above is worth anything: md-embed.jsx frames a document at a
      blob: URL with NO sandbox, so a Blob handed back with `text/html` on it —
      or with nothing on it, which is worse, because mdFileIsCraft answers no —
      would be a program running beside the journal, the passkey session and the
      store. A stranger's server cannot choose what a downloaded file claims to
      be if nobody ever asks it.

      `%PDF-` is the header every PDF has by specification. Anything else comes
      back as '' and mdFileGet answers null, which draws the same quiet slot a
      file nobody has yet draws. */
  const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d];   // "%PDF-"

  function mdFileSniff(bytes: unknown): string {
    const b = bytes as Uint8Array | null;
    if (!b || typeof b.length !== 'number' || b.length < PDF_MAGIC.length) return '';
    for (let i = 0; i < PDF_MAGIC.length; i++) {
      if (b[i] !== PDF_MAGIC[i]) return '';
    }
    return 'application/pdf';
  }

  /* ── naming what was dropped ──────────────────────────────────────────────
     Two sources, both factual, neither invented — the same discipline the media
     model follows for images.

     FIRST the document's own title. A PDF records one in its info dictionary,
     and that is what the author called it, which beats a filename somebody's
     download folder mangled into `2402.19255v3.pdf`.

     THEN the filename, minus its extension.

     The title is read by scanning for `/Title (...)`, and this deliberately does
     NOT parse PDF structure. A real parse means the xref table, object streams
     and compression — pdf.js, essentially, at a megabyte. This finds the title
     in a plain PDF and finds nothing in a compressed one, and finding nothing
     is a perfectly good outcome because the filename is right there. Cheap and
     sometimes right beats expensive and always right, when wrong costs nothing. */
  const PDF_TITLE = /\/Title\s*\(((?:[^()\\]|\\.)*)\)/;

  function mdFileTitleFromPdf(text: unknown): string {
    if (typeof text !== 'string') return '';
    const m = PDF_TITLE.exec(text);
    if (!m || typeof m[1] !== 'string') return '';
    /* PDF string escapes: \( \) \\ and the octal forms. Only the three that
       actually appear in titles are undone; anything else is left alone rather
       than half-decoded. */
    const raw = m[1].replace(/\\([()\\])/g, '$1').trim();
    /* A title full of control characters, or one that is really UTF-16 with a
       byte-order mark, is not a title we can show. Refuse rather than print
       mojibake into somebody's record. */
    if (!raw || /[\x00-\x08\x0e-\x1f�]/.test(raw)) return '';
    return raw.slice(0, NAME_MAX);
  }

  function mdFileNameOf(filename: unknown): string {
    const s = typeof filename === 'string' ? filename : '';
    return s.replace(/\.[a-z0-9]{1,8}$/i, '').replace(/[_]+/g, ' ').trim().slice(0, NAME_MAX);
  }

  /* A content hash, so the same document dropped twice is stored once. The same
     two-hash construction md-board.ts uses and for the same reason: one 32-bit
     hash over file-sized data collides more often than is comfortable, and a
     collision here means one document silently becoming another.

     IT IS NOT THE BLOB NAME AND MUST NEVER BECOME ONE. This answers "have I
     already got these bytes here?", which is a question only this device asks
     and only about names this device minted. A blob name is a promise made to
     strangers — a store, a proxy, another account's device — and FNV-1a, djb2
     and a length are all cheap to choose a preimage for. The blob name is the
     transport's SHA-256; md-transport.ts's header argues the split at length.

     It keeps two live callers, so this is not a fossil: it names a Craft, whose
     bytes do not leave the device, and it names a document on any build that
     cannot compute a SHA-256 — which is every build made before 1.9, and whose
     ids are written into records that must go on opening for ever. */
  function mdFileHash(bytes: Uint8Array): string {
    let a = 2166136261, b = 5381;
    for (let i = 0; i < bytes.length; i++) {
      const c = bytes[i] as number;      // in range by construction of the loop
      a = Math.imul(a ^ c, 16777619);
      b = ((b << 5) + b + c) | 0;
    }
    const u = (n: number) => (n >>> 0).toString(36);
    return `${u(a)}${u(b)}${bytes.length.toString(36)}`;
  }

  /* ── the store ────────────────────────────────────────────────────────────
     Every call opens the database and closes over it. IndexedDB is asynchronous
     and this is used a handful of times in a session — holding a connection
     open would mean handling `versionchange` from another tab, for no gain. */
  function open(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!g.indexedDB) { reject(new Error('no indexeddb')); return; }
      const req = g.indexedDB.open(nameOf(), DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('indexeddb open failed'));
      req.onblocked = () => reject(new Error('indexeddb blocked'));
    });
  }

  function tx(db: any, mode: string): any {
    return db.transaction(STORE, mode).objectStore(STORE);
  }

  function wrap(req: any): Promise<any> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('indexeddb request failed'));
    });
  }

  /* ── the store, and the door beside it ────────────────────────────────────
     Both halves are asked for defensively, the way md-backup.js asks: a build
     with no blob layer loaded is not broken, it is every build before today,
     and it must keep taking files exactly as it did.

     The SHA-256 comes from mdTransportHash rather than from crypto.subtle
     directly, and that is not indirection for its own sake: md-blobs.ts names
     what it keeps with that same function, so borrowing it is what guarantees
     a file cannot end up filed under one name and asked for under another. */
  async function storeName(bytes: Uint8Array): Promise<string> {
    if (typeof g.mdBlobPut !== 'function') return '';
    if (typeof g.mdTransportHash !== 'function') return '';
    let named: unknown = '';
    try { named = await g.mdTransportHash(bytes); } catch (e) { named = ''; }
    return (typeof named === 'string' && SHA256.test(named)) ? named : '';
  }

  /** Hand the bytes to the door. Never awaited by mdFilePut — see the header.

      The hash is passed as well as computed, because that option means "check
      me": bytes filed under a name they do not have fail every verification on
      every device for ever, and no part of the system can say why. */
  function offer(bytes: Uint8Array, ref: MdFileRef, owner: string): Promise<any> {
    const missed = { ok: false, reason: 'unreachable', hash: ref.hash || '' };
    if (!ref.hash || typeof g.mdBlobPut !== 'function') return Promise.resolve(missed);
    try {
      return Promise.resolve(g.mdBlobPut(bytes, {
        type: ref.type, hash: ref.hash, ref: owner || undefined,
      })).catch(() => missed);
    } catch (e) {
      return Promise.resolve(missed);
    }
  }

  /** Store a dropped file. Returns the reference a record should carry, or an
      error naming what was wrong — never a throw, because every one of these is
      something the UI has to be able to say out loud.

      `opts.ref` is the record that will OWN the bytes, `record:<id>`, when the
      caller knows one. A hold belongs to a record rather than to an account so
      that deleting one of six records holding the same document takes away one
      hold and not the document.

      `upload` is what the store made of it, and it settles later than this
      does. Ignoring it is the ordinary thing: the bytes are on this device
      before mdFilePut answers, and md-blobs.ts remembers what it still owes. */
  async function mdFilePut(file: any, opts?: any): Promise<
    { ok: true; ref: MdFileRef; upload: Promise<any> } | { ok: false; error: string }> {
    if (!file || typeof file.arrayBuffer !== 'function') return { ok: false, error: 'not_a_file' };
    if (!mdFileKind(file.type)) return { ok: false, error: 'unsupported_type' };
    if (file.size > MD_FILE_MAX_BYTES) return { ok: false, error: 'too_large' };
    let buf: ArrayBuffer;
    try { buf = await file.arrayBuffer(); } catch (e) { return { ok: false, error: 'unreadable' }; }
    const bytes = new Uint8Array(buf);

    /* Only what a RECORD may name is offered to a store — MD_FILE_TYPES, not
       mdFileKind. A Craft is a program and its bytes stay on the device that
       received them until story 1.10 decides otherwise, so it keeps the local
       name it has always had and nothing leaves. */
    const hash = MD_FILE_TYPES[file.type] ? await storeName(bytes) : '';
    const id = hash || mdFileHash(bytes);

    /* The title is read from the bytes we already have in hand, so no second
       read of the file and no wait the person can notice.

       Only for a PDF. Running a `/Title (…)` scan over an HTML document would
       find whatever happened to look like one, and a manifestation is named by
       its <title> — which md-manifest.ts already reads while taking the file
       apart, because that is a parse and this module does not parse HTML. */
    let title = '';
    if (mdFileKind(file.type) === 'pdf') {
      try {
        const head = new TextDecoder('latin1').decode(bytes);
        title = mdFileTitleFromPdf(head);
      } catch (e) { title = ''; }
    }
    const name = title || mdFileNameOf(file.name) || 'Document';
    const ref: MdFileRef = { id, name, type: file.type, size: file.size };
    if (hash) { ref.hash = hash; ref.blob = 1; }

    try {
      const db = await open();
      const have = await wrap(tx(db, 'readonly').get(id));
      if (!have) await wrap(tx(db, 'readwrite').put(new Blob([bytes], { type: file.type }), id));
      db.close();
    } catch (e) {
      /* Nothing has been offered to a store yet, so this really is the whole
         answer: the file is nowhere, and the person is told so. */
      return { ok: false, error: 'store_failed' };
    }
    return { ok: true, ref, upload: offer(bytes, ref, ownerOf(opts)) };
  }

  /** The bytes, or null when nowhere reachable has them — which is ordinary and
      not an error. It is the whole reason the record carries a name.

      This device first, always. A file dropped here opens with no network and
      no account, which is the promise that was true before there was a store
      and stays true now. Only then the store, and only for a name a store could
      have heard of — an id from before 1.9 costs no round trip to learn that
      nobody else has it. */
  async function mdFileGet(id: unknown): Promise<any> {
    if (typeof id !== 'string' || !id) return null;
    try {
      const db = await open();
      const blob = await wrap(tx(db, 'readonly').get(id));
      db.close();
      if (blob) return blob;
    } catch (e) { /* a database that will not open is not proof of anything */ }
    return fromStore(id);
  }

  /** The bytes from wherever md-blobs.ts can find them, as a Blob this app is
      willing to draw — or null.

      md-blobs.ts has already checked that what came back hashes to what was
      asked for; that is a check against a store handing over the WRONG bytes.
      This is the other one: the right bytes, being something other than a
      document. The Blob is built with the type the bytes themselves say, so
      there is no path by which a store's answer decides what a frame draws. */
  async function fromStore(id: string): Promise<any> {
    if (!mdFileTravels(id) || typeof g.mdBlobGet !== 'function') return null;
    let bytes: any = null;
    try { bytes = await g.mdBlobGet(id); } catch (e) { bytes = null; }
    if (!bytes || typeof bytes.length !== 'number') return null;
    const type = mdFileSniff(bytes);
    if (!type) return null;
    /* Not written into the database above. The door keeps its own cache, so a
       second read is local either way, and a copy here would be a second copy
       of a file this device did not make. */
    return new Blob([bytes], { type });
  }

  async function mdFileDelete(id: unknown): Promise<boolean> {
    if (typeof id !== 'string' || !id) return false;
    try {
      const db = await open();
      await wrap(tx(db, 'readwrite').delete(id));
      db.close();
      return true;
    } catch (e) {
      return false;
    }
  }

  /** Take a cancelled upload's name out of the record that owned it.

      The blob door cannot do this itself: it knows a ref is `record:file001`
      and must never learn that the record might be in either of two day stores,
      or that its file is one line of markdown. This module already owns that
      grammar and asks md-store's whole-map door to write, so the CRDT beside a
      locally edited day advances with the document instead of resurrecting the
      line on the next merge.

      The local bytes are NOT deleted here. Two records can name one content
      hash and the file database has no per-record ledger; deleting the shared
      copy to tidy one cancellation would make the other record fail offline. */
  function mdFileForgetRecord(hash: unknown, owner: unknown): any {
    if (typeof hash !== 'string' || !SHA256.test(hash)) return { ok: false, forgot: 0 };
    const match = /^record:([a-z0-9]{1,64})$/.exec(typeof owner === 'string' ? owner : '');
    if (!match || typeof g.mdStoreAll !== 'function' || typeof g.mdStoreWriteAll !== 'function') {
      return { ok: false, forgot: 0 };
    }
    const uid = match[1];
    let forgot = 0;
    let landed = true;

    const namesHash = (line: string): boolean => {
      if (typeof g.mdEmbedLine === 'function' && typeof g.mdEmbedFileId === 'function') {
        const embed = g.mdEmbedLine(line);
        if (embed && g.mdEmbedFileId(embed.target) === hash) return true;
      }
      return line.indexOf(`mdfile:${hash}`) >= 0;
    };

    for (const kind of ['events', 'records']) {
      const held = g.mdStoreAll(kind);
      const next: any = { ...(held || {}) };
      let changed = false;
      for (const [day, rows] of Object.entries(held || {})) {
        if (!Array.isArray(rows)) continue;
        next[day] = rows.map((row: any) => {
          if (!row || row.uid !== uid) return row;
          const body = typeof row.body === 'string' ? row.body : '';
          const lines = body.split('\n');
          const kept = lines.filter((line: string) => !namesHash(line));
          const legacy = row.file && typeof row.file === 'object'
            && (row.file.hash === hash || row.file.id === hash);
          if (kept.length === lines.length && row.hash !== hash && !legacy) return row;
          const clean = { ...row, body: kept.join('\n') };
          if (clean.hash === hash) { delete clean.hash; delete clean.blob; }
          if (legacy) delete clean.file;
          forgot += 1;
          changed = true;
          return clean;
        });
      }
      if (changed && !g.mdStoreWriteAll(kind, next)) landed = false;
    }
    return { ok: landed, forgot: landed ? forgot : 0 };
  }

  g.MD_FILE_MAX_BYTES = MD_FILE_MAX_BYTES;
  g.MD_CRAFT_MIME = MD_CRAFT_MIME;
  g.mdFileKind = mdFileKind;
  g.mdFileIsCraft = mdFileIsCraft;
  g.MD_MANIFEST_MIME = MD_CRAFT_MIME;
  g.mdFileIsManifest = mdFileIsCraft;
  g.mdFileRefOk = mdFileRefOk;
  g.mdFileHash = mdFileHash;
  g.mdFileTravels = mdFileTravels;
  g.mdFileSniff = mdFileSniff;
  g.mdFileTitleFromPdf = mdFileTitleFromPdf;
  g.mdFileNameOf = mdFileNameOf;
  g.mdFilePut = mdFilePut;
  g.mdFileGet = mdFileGet;
  g.mdFileDelete = mdFileDelete;
  g.mdFileForgetRecord = mdFileForgetRecord;

  // CommonJS escape hatch, following md-media.ts and md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_FILE_MAX_BYTES, MD_CRAFT_MIME, MD_MANIFEST_MIME: MD_CRAFT_MIME,
      mdFileKind, mdFileIsCraft, mdFileIsManifest: mdFileIsCraft,
      mdFileRefOk, mdFileHash, mdFileTravels, mdFileSniff,
      mdFileTitleFromPdf, mdFileNameOf, mdFilePut, mdFileGet, mdFileDelete,
      mdFileForgetRecord };
  }
})(typeof window !== 'undefined' ? window : globalThis);
